import { useEffect, useState } from "react";

import { createAxiosInstance } from "@moodys/mdc-frontend.services.fetcher";
import { logger } from "@moodys/mdc-frontend.services.logger";

export type UseAEMContentArgs<T> = {
  contentUrl: string;
  fallbackContent: T;
  logError?: boolean;
};

const axiosInstance = createAxiosInstance({
  disableAuthorizer: true,
});

export const useAEMContent = <T>({
  contentUrl,
  fallbackContent,
  logError = true,
}: UseAEMContentArgs<T>) => {
  const [content, setContent] = useState<T>(fallbackContent);

  useEffect(() => {
    const abortController = new AbortController();

    const getContent = async () => {
      try {
        const { data } = await axiosInstance.get<T>(contentUrl, {
          signal: abortController.signal,
        });
        setContent(data);
      } catch (error) {
        if (!abortController.signal.aborted && logError) {
          logger.error({
            error,
            message: `Error fetching content from ${contentUrl}. Using fallback content.`,
          });
          setContent(fallbackContent);
        }
      }
    };

    getContent();

    return () => {
      abortController.abort();
    };
  }, [contentUrl, fallbackContent, logError]);

  return content;
};
